import type { ID, Rarity, Variant, LocalizedString } from './common';

/** Dynamiczny powód, dla którego pet nie może teraz brać udziału w akcjach. */
export type PetBusyReason = 'stall' | 'job';

/**
 * Definicja typu peta (statyczna, z configu).
 *
 * Jeden PetDef = jeden asset graficzny.
 * Warianty (gold / rainbow / huge) są generowane wizualnie w runtime:
 *  - 'normal'  → renderujemy textureKey 1:1
 *  - 'gold'    → ten sam sprite + złota ramka + delikatny złoty glow
 *  - 'rainbow' → ten sam sprite + tęczowa ramka + animowany hue-shift glow
 *  - 'huge'    → osobny, większy sprite (dodawany w przyszłości jako 'hugeTextureKey')
 *
 * Dzięki temu z 60 petów w pliku PETS gracz w grze może zdobyć 180 unikalnych
 * wariantów (60 normal + 60 gold + 60 rainbow) bez powielania assetów.
 */
export interface PetDef {
  id: ID;
  name: LocalizedString;
  rarity: Rarity;

  /** Bazowa moc wariantu 'normal'. Gold = ×1.6, Rainbow = ×3.2, Huge = ×25, Titanic = ×100 (z VARIANT_MULT). */
  basePower: number;

  origin: {
    eggIds?: ID[];   // z których jajek może wypaść
    biomeId?: ID;    // do którego biomu należy (MVP: 'biome-meadow')
    eventId?: ID;    // jeśli pet jest eventowy
  };

  art: {
    /**
     * Klucz tekstury Phaser. Dla MVP = nazwa pliku PNG bez rozszerzenia,
     * np. 'pet_meadow_bunny'.
     * Ładujemy z public/assets/pets/ w PreloadScene.
     */
    textureKey: string;

    /**
     * Opcjonalne klucze animacji (gdy w przyszłości dodamy spritesheet'y).
     * W MVP zostawiamy puste — pet używa pojedynczej klatki z tweenowanym
     * idle-bobbingiem (sin(t) na pozycji Y) i lekkim mirror'em na walk.
     */
    animations?: {
      idle?: string;
      walk?: string;
      attack?: string;
    };

    /** Klucz tekstury dla wariantu 'huge' (większy, osobny asset). Dodamy później. */
    hugeTextureKey?: string;

    /** Mnożnik skali sprite'a. Default 1. */
    scale?: number;

    /** Czy rysować cień pod petem. Default true. */
    shadow?: boolean;
  };

  audio?: {
    onAttack?: string;
    onCollect?: string;
  };

  tags?: string[];
}

/**
 * Konkretna sztuka peta w inventory gracza (jedna sztuka = jeden uid).
 */
export interface PetInstance {
  uid: string;          // UUID, generowany przy zdobyciu
  defId: ID;            // ref do PetDef.id
  variant: Variant;
  acquiredAt: number;   // epoch ms
  locked?: boolean;     // zablokowany przed mergem/sprzedażą
  favorite?: boolean;
  exp?: number;         // pod przyszłe leveling, w MVP = 0
}

/**
 * Numerical ordering rarity (do sortowania w UI).
 * Wyższa liczba = bardziej rzadkie/cenne.
 */
export const RARITY_ORDER: Record<Rarity, number> = {
  common: 1,
  uncommon: 2,
  rare: 3,
  epic: 4,
  legendary: 5,
  mythic: 6,
};

/**
 * Numerical ordering variant (do sortowania w UI).
 * Wyższa liczba = bardziej cenne (titanic > huge > rainbow > gold > normal).
 */
export const VARIANT_ORDER: Record<Variant, number> = {
  normal: 1,
  gold: 2,
  rainbow: 3,
  huge: 4,
  titanic: 5,
};

/**
 * Mnożniki mocy wariantów. Zmieniaj tutaj balans wariantów dla CAŁEJ gry.
 * Titanic (×100) = apex rozmiarowy, 4× silniejszy od huge (×25) — patrz
 * Pet Simulator: Titanic > Huge. Sterowanie skalą wizualną: HUGE/TITANIC_SCALE_MULT.
 */
export const VARIANT_MULT: Record<Variant, number> = {
  normal: 1,
  gold: 1.6,
  rainbow: 3.2,
  huge: 25,
  titanic: 100,
};

/**
 * Bonus do ZŁOTA ("coin find") wnoszony przez KAŻDEGO założonego peta danego
 * wariantu. Wartość = dodatkowy procent w ułamku (0.20 = +20%).
 *
 * Bonusy wszystkich equipped petów SUMUJĄ się, a suma mnoży złoto z każdej
 * destrukcji (obok upgrade'u `coin_mult`). Przykład: 6× rainbow → +300% → ×4
 * złota z każdego obiektu. Liczone w `WorldScene.recalcGoldRainbowCoinBonus()`,
 * aplikowane w `onDestructibleDestroyed`.
 *
 * Oś "gold/rainbow" = bonus złota. Oś "rozmiar" (huge/titanic) zostaje
 * czystą mocą (patrz VARIANT_MULT) — dlatego mają 0. Zmieniaj balans złota
 * wariantów gold/rainbow TYLKO tutaj (single source of truth).
 */
export const VARIANT_COIN_BONUS: Record<Variant, number> = {
  normal: 0,
  gold: 0.20,
  rainbow: 0.50,
  huge: 0,
  titanic: 0,
};

/**
 * Konfiguracja vizualnych nakładek wariantów (renderer używa tych wartości).
 * Tu trzymamy kolory ramki / glow, żeby było jedno źródło prawdy.
 */
export const VARIANT_VISUALS: Record<
  Variant,
  {
    frameColor: string;       // kolor ramki/obwódki wokół peta
    glowColor: string;        // kolor poświaty
    glowAlpha: number;        // siła poświaty 0..1
    hueShift?: boolean;       // czy animować przesunięcie odcienia (rainbow)
    sparkles?: boolean;       // czy dodać particle iskierek
  }
> = {
  normal: {
    frameColor: '#00000000',  // brak ramki
    glowColor: '#00000000',
    glowAlpha: 0,
  },
  gold: {
    frameColor: '#FFD24A',
    glowColor: '#FFE48A',
    glowAlpha: 0.55,
    sparkles: true,
  },
  rainbow: {
    frameColor: '#FF69B4',    // bazowy odcień, hue-shift go animuje
    glowColor: '#FFFFFF',
    glowAlpha: 0.7,
    hueShift: true,
    sparkles: true,
  },
  huge: {
    frameColor: '#FFFFFF',
    glowColor: '#FFFAA0',
    glowAlpha: 0.6,
  },
  titanic: {
    // Apex rozmiarowy — kosmiczny fiolet/biel, mocniejszy glow niż huge.
    frameColor: '#C9A7FF',
    glowColor: '#FFFFFF',
    glowAlpha: 0.75,
  },
};
